import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormComponent, ProvincesStatesCodesService, ValueValidator, TableView, SecurityService } from '@tdwb/core';

import {
  AcceptedAgreements,
  Agreement,
  AgreementStatus,
  AgreementType,
  ClientInfoFormData
} from '@tdwb/lazy/platform/preferences';

export interface SelectedAgreement {
  [key: string]: boolean;
}

@Component({
  selector: 'td-wb-consolidated-agreements-form',
  templateUrl: 'consolidated-agreements-form.component.html',
  styleUrls: ['consolidated-agreements-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConsolidatedAgreementsFormComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  private static readonly TERMS_CONDITION_TYPES: ReadonlyArray<AgreementType> =
    [AgreementType.TDW_TERMS_AND_CONDITIONS, AgreementType.TDW_TERMS_AND_CONDITIONS_LITE];

  @Input() agreements: Array<Agreement> = [];
  @Input() updateAgreements?: boolean = false;
  @Input() savedAgreements?: Array<Agreement>;
  @Output() readonly agreementFormChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonly agreementAllChecked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonly acceptedAgreements: EventEmitter<AcceptedAgreements> = new EventEmitter<AcceptedAgreements>();

  exchangeAgreements: Array<Agreement> = [];
  agreementGroup: Array<Agreement> = [];
  selectedExchangeAgreement: SelectedAgreement = {};
  signedPreferenceAgreements: Array<Agreement> = [];
  unsignedPreferenceAgreements: Array<Agreement> = [];
  allChecked: boolean;
  acknowledgementChecked: boolean = false;
  submitted: boolean;
  view: TableView;
  formData: ClientInfoFormData;
  collapsed: boolean = true;
  anyAgreementChecked: boolean = false;
  isAgentTitle: boolean;
  agreementUnsigned: boolean = true;
  fplPermissonEnabled: boolean = false;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor(public provinceStates: ProvincesStatesCodesService,
              private securityService: SecurityService,
              private changeDetectorRef: ChangeDetectorRef,
              private form: FormComponent) {
  }

  ngOnInit(): void {
    this.fplPermissonEnabled = this.securityService.hasPermission('FULLY_PAID_LENDING');
    this.exchangeAgreements = this.getExchangeAgreements();
    if (this.updateAgreements ) {
      this.initPreferenceAgreements();
    } else {
      this.initFirstTimeFlowAgreements();
    }
    this.initFormData();
    if (this.form.formElement !== undefined) {
      this.form.formElement.valueChanges.subscribe((): void => {
        if (!this.form.pristine && this.submitted) {
          this.agreementFormChange.emit(false);
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.form.submitAction
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        this.updateAgreementStatus();
        this.submitted = true;
        if (this.anyAgreementChecked && this.acknowledgementChecked) {
          const signedAgreements = this.agreementGroup.filter((agreement: Agreement): boolean =>
            agreement.agreementStatus === AgreementStatus.ACCEPTED);
          this.acceptedAgreements.emit({ clientInfo: this.formData, agreements: signedAgreements });
        }
      });
  }

  ngOnChanges(): void {
    this.removeSavedAgreements();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onAgreementChange(agreement: Agreement): void {
    const agreementGroupItem = {
      agreementType:  agreement.agreementType,
      agreementStatus: this.selectedExchangeAgreement[agreement.agreementType] ?
        AgreementStatus.ACCEPTED : AgreementStatus.UNSIGNED
    };
    const found = this.agreementGroup.find((item: Agreement): boolean =>
      item.agreementType === agreement.agreementType);

    if (found !== undefined) {
      this.agreementGroup.splice(this.agreementGroup.indexOf(found), 1);
      if (this.updateAgreements) {
        this.agreementGroup.push(agreementGroupItem);
      }
    } else {
      this.agreementGroup.push(agreementGroupItem);
    }

    this.allChecked = !this.agreementGroup.some((item: Agreement): boolean => item.agreementStatus === AgreementStatus.UNSIGNED) &&
      (this.agreementGroup.length === this.exchangeAgreements.length ||
        (this.updateAgreements && this.agreementGroup.length === this.unsignedPreferenceAgreements.length));
    this.updateAgreementStatus();
    this.changeDetectorRef.detectChanges();
  }

  onAllAgreementsChange(allChecked: boolean): void {
    this.agreementGroup = [];
    if (this.updateAgreements) {
      this.unsignedPreferenceAgreements.forEach((agreement: Agreement): void => {
        this.updateAllAgreement(agreement, allChecked);
      });
    } else {
      this.exchangeAgreements.forEach((agreement: Agreement): void => {
        this.updateAllAgreement(agreement, allChecked);
      });
    }
    this.updateAgreementStatus();
    this.changeDetectorRef.detectChanges();
  }

  createCheckboxValidator(value?: boolean): ValueValidator {
    return (): ValidationErrors | null => {
      if (!value) {
        return { required: true };
      }
      return null;
    };
  }

  checkName(displayCheck?: boolean): boolean {
    if (displayCheck) {
      return this.findAgreements(this.unsignedPreferenceAgreements,
        AgreementType.AMEX, AgreementType.NYSE, AgreementType.NASDAQ, AgreementType.OPRA,
        AgreementType.USOTC, AgreementType.CMSPDJ, AgreementType.LSEGRU)?.length > 0;
    } else {
      const result = this.findAgreements(this.agreementGroup,
        AgreementType.AMEX, AgreementType.NYSE, AgreementType.NASDAQ, AgreementType.OPRA,
        AgreementType.USOTC, AgreementType.CMSPDJ, AgreementType.LSEGRU);
      return  this.isSomeAgreementAccepted(result);
    }
  }

  checkNASDAQFields(displayCheck?: boolean): boolean {
    if (displayCheck) {
      return this.findAgreements(this.unsignedPreferenceAgreements, AgreementType.NASDAQ)?.length > 0;
    } else {
      const result = this.findAgreements(this.agreementGroup, AgreementType.NASDAQ);
      return this.isSomeAgreementAccepted(result);
    }
  }

  checkQualifyAsNonProfessional(displayCheck?: boolean): boolean {
    if (displayCheck) {
      return this.findAgreements(this.unsignedPreferenceAgreements, AgreementType.NASDAQ,
        AgreementType.USOTC)?.length > 0;
    } else {
      const result = this.findAgreements(this.agreementGroup, AgreementType.NASDAQ, AgreementType.USOTC);
      return this.isSomeAgreementAccepted(result);
    }
  }

  checkAddress(displayCheck?: boolean): boolean {
    if (displayCheck) {
      return this.findAgreements(this.unsignedPreferenceAgreements,
        AgreementType.AMEX, AgreementType.NYSE, AgreementType.OPRA,
        AgreementType.USOTC, AgreementType.CMSPDJ, AgreementType.LSEGRU)?.length > 0;
    } else {
      const result = this.findAgreements(this.agreementGroup, AgreementType.AMEX,
        AgreementType.NYSE, AgreementType.OPRA, AgreementType.USOTC, AgreementType.CMSPDJ, AgreementType.LSEGRU);
      return this.isSomeAgreementAccepted(result);
    }
  }

  checkOccupation(displayCheck?: boolean): boolean {
    if (displayCheck) {
      return this.findAgreements(this.unsignedPreferenceAgreements, AgreementType.AMEX, AgreementType.NYSE,
        AgreementType.USOTC)?.length > 0;
    } else {
      const result = this.findAgreements(this.agreementGroup, AgreementType.AMEX, AgreementType.NYSE,
        AgreementType.USOTC);
      return this.isSomeAgreementAccepted(result);
    }
  }

  checkTitle(displayCheck?: boolean): boolean {
    return this.checkOccupation(displayCheck);
  }

  checkEmployer(displayCheck?: boolean): boolean {
    if (displayCheck) {
      return this.findAgreements(this.unsignedPreferenceAgreements, AgreementType.AMEX, AgreementType.NYSE)?.length > 0;
    } else {
      const result = this.findAgreements(this.agreementGroup, AgreementType.AMEX, AgreementType.NYSE);
      return this.isSomeAgreementAccepted(result);
    }
  }

  checkAgentUseTitleValidator(isNasdaqChecked: boolean): boolean {
    return isNasdaqChecked ? this.checkAgentTitle() : false;
  }

  checkAgentNameValidator(isNasdaqChecked: boolean):  boolean {
    return  isNasdaqChecked ? this.checkAgentName() : false;
  }

  checkPostalCode(provinceState: string): string {
    if (this.checkAddress() && provinceState !== '') {
      return provinceState;
    }
    return undefined;
  }

  onIconClick(): void {
    this.collapsed = !this.collapsed;
  }

  getIconId(): string {
    return this.collapsed ? 'expand' : 'collapse';
  }

  getTermsConditions(): Agreement {
    return this.agreements
      .find((item: Agreement): boolean => ConsolidatedAgreementsFormComponent.TERMS_CONDITION_TYPES.indexOf(item.agreementType) >= 0);
  }

  getStockLendingAgreement(): Agreement {
    return {
      agreementType: AgreementType.STOCK_LENDING,
      agreementStatus: AgreementStatus.ACCEPTED
    };
  }

  isNasdaqChecked(): boolean {
    return Object.keys(this.selectedExchangeAgreement).length > 0
      && (this.selectedExchangeAgreement[AgreementType.NASDAQ] || this.selectedExchangeAgreement[AgreementType.USOTC]);
  }

  displayCheck(checkResult: boolean): boolean {
    return !this.updateAgreements || this.updateAgreements && checkResult;
  }

  displayOptionalText(checkResult: boolean): boolean {
    return !this.updateAgreements && !checkResult;
  }

  disablePreferenceAgreements(agreementType: AgreementType): boolean {
    if (this.savedAgreements !== undefined) {
      this.savedAgreements.forEach((agreement: Agreement): number => this.signedPreferenceAgreements.push(agreement));
    }
    return this.updateAgreements &&
      this.signedPreferenceAgreements.some((agreement: Agreement): boolean => agreement.agreementType === agreementType);
  }

  private updateAgreementStatus(): void {
    this.agreementAllChecked.emit(this.allChecked);
    this.anyAgreementChecked = this.isSomeAgreementAccepted(this.agreementGroup);
  }

  private getExchangeAgreements(): Array<Agreement> {
    return this.agreements
      .filter((item: Agreement): boolean =>  ConsolidatedAgreementsFormComponent.TERMS_CONDITION_TYPES.indexOf(item.agreementType) < 0);
  }

  private findAgreements<T extends { agreementType: AgreementType }>(agreements: Array<T>, ...types: Array<AgreementType> ): Array<T> {
    return agreements.filter((item: T): boolean => types.indexOf(item.agreementType) >= 0);
  }

  private isSomeAgreementAccepted(items: Array<{ agreementStatus: AgreementStatus}>): boolean {
    return items.some((item: { agreementStatus: AgreementStatus}): boolean => item.agreementStatus === AgreementStatus.ACCEPTED);
  }

  private initFirstTimeFlowAgreements(): void {
    this.allChecked = true;
    this.exchangeAgreements.forEach((agreement: Agreement): void => {
      this.selectedExchangeAgreement[agreement.agreementType] = true;
      this.updateAllAgreement(agreement, this.allChecked);
    });
    this.updateAgreementStatus();
  }

  private initPreferenceAgreements(): void {
    this.agreementUnsigned = this.agreements.some((agreement: Agreement): boolean =>
      agreement.agreementStatus === AgreementStatus.UNSIGNED);
    this.agreementGroup = [];
    this.exchangeAgreements.forEach((agreement: Agreement): void => {
      if (agreement.agreementStatus === AgreementStatus.ACCEPTED) {
        this.initPreferenceSelectedAgreement(agreement);
      } else {
        this.selectedExchangeAgreement[agreement.agreementType] = false;
        this.unsignedPreferenceAgreements.push(agreement);
        this.agreementGroup.push(agreement);
      }
    });
    this.allChecked = this.agreementGroup.length === 0;
  }

  private initFormData(): void {
    this.formData = {
      clientFirstName: '',
      clientLastName: '',
      signature: '',
      clientAddress: '',
      city: '',
      provinceState: '',
      postalCode: '',
      clientOccupation: '',
      clientEmployerNameAddress: '',
      clientTitlePosition: '',
      clientEmployerFunction: '',
      professionalIndicator: undefined,
      titles: {
        CEO: false,
        COO: false,
        DIRECTOR: false,
        PRESIDENT: false,
        SVP: false,
        VP: false
      },
      agentName: '',
      agentTitle: ''
    };
  }

  private initPreferenceSelectedAgreement(agreement: Agreement): void {
    const unselectedAgreement = {
      agreementType:  agreement.agreementType,
      agreementStatus: agreement.agreementStatus
    };
    this.selectedExchangeAgreement[agreement.agreementType] = true;
    this.signedPreferenceAgreements.push(unselectedAgreement);
  }

  private checkAgentName(): boolean {
    const isAgentNameEmpty = this.formData.agentName?.length === 0 || this.formData.agentName?.length === undefined;

    return (this.isAgentTitleSelected() && isAgentNameEmpty ) || (this.formData.agentTitle?.length > 0 &&
        isAgentNameEmpty);
  }

  private checkAgentTitle(): boolean {
    return (!this.isAgentTitleSelected() && this.formData.agentName?.length > 0) || (!this.isAgentTitleSelected() &&
        this.formData.agentTitle?.length > 0);
  }

  private isAgentTitleSelected(): boolean {
    return Object.keys(this.formData.titles).find((key: string): boolean => this.formData.titles[key] === true) !== undefined;
  }

  private updateAllAgreement(agreement: Agreement, allChecked: boolean): void {
    this.selectedExchangeAgreement[agreement.agreementType] = allChecked;
    const agreementGroupItem = {
      agreementType:  agreement.agreementType,
      agreementStatus: this.selectedExchangeAgreement[agreement.agreementType] ?
        AgreementStatus.ACCEPTED : AgreementStatus.UNSIGNED
    };
    if (allChecked) {
      this.agreementGroup.push(agreementGroupItem);
    }
  }

  private removeSavedAgreements(): void {
    if (this.savedAgreements !== undefined) {
      this.savedAgreements.forEach((agreement: Agreement): void => {
        this.refreshAgreementsList(agreement, this.agreementGroup);
        this.refreshAgreementsList(agreement, this.unsignedPreferenceAgreements);
      });
      this.agreementUnsigned = this.unsignedPreferenceAgreements.length !== 0;
      this.allChecked = !this.agreementUnsigned;
    }
  }

  private refreshAgreementsList(savedAgreement: Agreement, agreements: Array<Agreement>): void {
    const found = agreements.find((item: Agreement): boolean =>
      item.agreementType === savedAgreement.agreementType);
    if (found !== undefined) {
      agreements.splice(agreements.indexOf(found), 1);
    }
  }
}
